import {css, styled} from '@volkswagen-onehub/components-core';
import {keyframes} from 'styled-components';

import * as React from 'react';
import {ON_TIMER_END_EVENT} from './countdown';
import {makeClip} from '../helpers';
import {useSectionContext} from '../../../context';
import {TransitionState} from '../../../modules/editorial/sections/PremiereSection';

const move = keyframes`
	0% {
		transform: translateY(0%);
	}
	100% {
		transform: translateY(-100%);
	}
`;

const StyledTransitionWrapper = styled.div<{
    transitionState: 'before' | 'transition' | 'after';
}>`
    ${props =>
        props.transitionState === 'transition' &&
        css`
            animation: ${move} 1000ms ease-in-out;
        `}
`;

const StyledBefore = styled.div<{transitionState: TransitionState}>`
    ${props => props.transitionState === 'after' && `display:none`}
`;

const StyledAfter = styled.div<{transitionState: TransitionState}>`
    display: ${props => (props.transitionState === 'before' ? 'none' : '')};

    ${props =>
        props.transitionState !== 'after' &&
        `position: absolute;
    inset: 0;
    top: 100%;`}
`;

const StyledPremiereSection = styled.div`
    overflow: hidden;
    height: 100%;
`;

const StyledHiddenHeading = styled.h2`
    ${makeClip};
`;

interface PremiereSectionProps {
    beforeReveal: React.ReactElement;
    afterReveal: React.ReactElement;
    beforeState: boolean;
    timeDistance: number;
    showHiddenHeadline: boolean;
    transitionState: TransitionState;
    setTransitionState: React.Dispatch<React.SetStateAction<TransitionState>>;
}

export const PremiereSection = (props: PremiereSectionProps) => {
    const {
        beforeState,
        beforeReveal,
        afterReveal,
        timeDistance,
        showHiddenHeadline,
        transitionState,
        setTransitionState
    } = props;

    // current before State
    const beforeStateNow = timeDistance > 0;

    const ref = React.useRef<HTMLDivElement>(null);

    const sectionContext = useSectionContext();

    React.useEffect(() => {
        if (!beforeStateNow && beforeState) {
            // ssr was before and now we are after reveal -> transition immediately
            setTransitionState('transition');
            return;
        }
        if (!beforeStateNow) {
            // we are already past reveal. No need to add listener
            return;
        }
        const element = ref.current;

        // eslint-disable-next-line no-unused-expressions
        element?.addEventListener(ON_TIMER_END_EVENT, () =>
            setTransitionState('transition')
        );
        return () => {
            // eslint-disable-next-line no-unused-expressions
            element?.removeEventListener(ON_TIMER_END_EVENT, () =>
                setTransitionState('transition')
            );
        };
    }, [beforeState, beforeStateNow, setTransitionState]);

    return (
        <StyledPremiereSection key="premiere" ref={ref}>
            {/* adding this heading helps screen reader users find the section announced by the live region easier since it would appear in the heading navigation.
            It has the same value as the section's aria-label, so users can find the premiere section both in the landmarks and the headings navigation */}
            {showHiddenHeadline && sectionContext?.sectionAriaLabel ? (
                <StyledHiddenHeading>
                    {sectionContext.sectionAriaLabel}
                </StyledHiddenHeading>
            ) : null}
            <StyledTransitionWrapper
                transitionState={transitionState}
                onAnimationEnd={() => setTransitionState('after')}
            >
                <StyledBefore transitionState={transitionState}>
                    {beforeReveal}
                </StyledBefore>
                <StyledAfter transitionState={transitionState}>
                    {afterReveal}
                </StyledAfter>
            </StyledTransitionWrapper>
        </StyledPremiereSection>
    );
};
